import {
    Alert,
    Anchor,
    Button,
    Group,
    Modal,
    NumberInput,
    Paper,
    Select,
    Stack,
    Text,
} from '@mantine/core'
import {DateInput, TimeInput} from '@mantine/dates'
import {Link} from '@tanstack/react-router'
import {format, startOfDay} from 'date-fns'
import {useEffect, useState} from 'react'

import {businessCalendarDate, formatBusinessTimestamp} from 'src/api/business-time'
import {useCreateCustomerSavedLocation} from 'src/customers/saved-locations/use-saved-locations'
import {formatJobDate, toLocalDate} from 'src/jobs/job-schedule-format'
import {LocationEditModal} from 'src/jobs/location-edit-modal'
import type {JobLocation} from 'src/jobs/types'
import type {ProjectJobSummary} from 'src/projects/schemas'

import type {ProjectPendingEquipmentRow} from './schemas'
import {useDeployProjectEquipment} from './use-deploy-project-equipment'
import {useDeployableLocations} from './use-deployable-locations'

type DeployEquipmentDialogProps = {
    opened: boolean
    onClose: () => void
    onDeployed: () => void
    projectId: string
    customerId: string
    jobs: ProjectJobSummary[]
    pendingRow: ProjectPendingEquipmentRow | null
}

const ADD_NEW_LOCATION_VALUE = '__add_new__'

function formatRentalTermsLabel(row: {
    billingTermsMixed?: boolean
    billingType?: string | null
    billingRate?: string | null
}) {
    if (row.billingTermsMixed) {
        return 'Mixed'
    }
    if (row.billingType == null || row.billingRate == null) {
        return 'Not set'
    }
    return `$${row.billingRate} · ${row.billingType === 'per_load' ? 'per load' : row.billingType}`
}

function toCreateSavedLocationInput(location: JobLocation) {
    return {
        name: location.name,
        latitude: location.latitude ?? undefined,
        longitude: location.longitude ?? undefined,
        fullAddress: location.addressText ?? undefined,
        notes: location.notes || undefined,
    }
}

export function DeployEquipmentDialog({
    opened,
    onClose,
    onDeployed,
    projectId,
    customerId,
    jobs,
    pendingRow,
}: DeployEquipmentDialogProps) {
    const deployEquipment = useDeployProjectEquipment(projectId)
    const createSavedLocation = useCreateCustomerSavedLocation(customerId)

    const [jobId, setJobId] = useState<string | null>(null)
    const [quantity, setQuantity] = useState<number | string>(1)
    const [locationId, setLocationId] = useState<string | null>(null)
    const [deployedOn, setDeployedOn] = useState<Date>(businessCalendarDate())
    const [deployedTime, setDeployedTime] = useState(formatBusinessTimestamp(new Date(), 'HH:mm'))
    const [locationModalOpen, setLocationModalOpen] = useState(false)
    const [formError, setFormError] = useState<string | null>(null)

    const {options: locationOptions} = useDeployableLocations({
        jobId,
        customerId,
        enabled: opened,
    })

    useEffect(() => {
        if (!opened || !pendingRow) {
            return
        }
        setJobId(pendingRow.jobId)
        setQuantity(pendingRow.quantity)
        setLocationId(null)
        setDeployedOn(businessCalendarDate())
        setDeployedTime(formatBusinessTimestamp(new Date(), 'HH:mm'))
        setFormError(null)
    }, [opened, pendingRow])

    function handleClose() {
        if (deployEquipment.isPending) {
            return
        }
        onClose()
    }

    async function handleSaveNewLocation(location: JobLocation) {
        const created = await createSavedLocation.mutateAsync(toCreateSavedLocationInput(location))
        setLocationId(created.locationId)
    }

    async function handleSubmit() {
        if (!pendingRow || !jobId || !locationId) {
            return
        }

        setFormError(null)
        const parsedQuantity = typeof quantity === 'number' ? quantity : Number(quantity)

        try {
            await deployEquipment.mutateAsync({
                jobId,
                locationId,
                deployedOn,
                deployedTime,
                rentalAssetId: pendingRow.rentalAssetId ?? undefined,
                rentalAssetTypeId: pendingRow.rentalAssetId
                    ? undefined
                    : pendingRow.rentalAssetTypeId,
                quantity: pendingRow.rentalAssetId ? undefined : parsedQuantity,
                thirdPartyRentalAssetOfferingId: pendingRow.rentalAssetId
                    ? undefined
                    : (pendingRow.thirdPartyRentalAssetOfferingId ?? undefined),
            })
            onDeployed()
            handleClose()
        } catch (error) {
            setFormError(error instanceof Error ? error.message : 'Deployment failed')
        }
    }

    const maxQuantity = pendingRow?.quantity ?? 1
    const parsedQuantity = typeof quantity === 'number' ? quantity : Number(quantity)
    const quantityValid =
        Number.isInteger(parsedQuantity) && parsedQuantity >= 1 && parsedQuantity <= maxQuantity
    const isBackdated = deployedOn < businessCalendarDate()
    const startsAfterToday =
        pendingRow != null && startOfDay(pendingRow.jobScheduledDate) > businessCalendarDate()
    const canSubmit =
        pendingRow?.deployable === true &&
        jobId != null &&
        locationId != null &&
        (pendingRow.rentalAssetId != null || quantityValid)

    const equipmentLabel =
        pendingRow?.rentalAssetId != null
            ? (pendingRow.assetDisplayName ?? pendingRow.rentalAssetTypeName)
            : pendingRow?.rentalAssetTypeName

    return (
        <>
            <Modal opened={opened} onClose={handleClose} title="Deploy equipment" size="lg">
                <Stack gap="md">
                    {formError ? (
                        <Alert color="red" title="Unable to deploy equipment">
                            <Stack gap="xs">
                                <Text>{formError}</Text>
                                {pendingRow?.rentalAssetId ? (
                                    <Anchor
                                        component={Link}
                                        to="/rental-assets/$assetId"
                                        params={{assetId: pendingRow.rentalAssetId}}
                                    >
                                        View equipment history
                                    </Anchor>
                                ) : null}
                            </Stack>
                        </Alert>
                    ) : null}

                    {pendingRow?.notReadyReason ? (
                        <Alert color="orange" title="Not ready to deploy">
                            {pendingRow.notReadyReason}
                        </Alert>
                    ) : pendingRow?.deployable === false ? (
                        <Alert color="orange" title="Billing terms required">
                            Deployment would create a rental stint that never bills. Set billing
                            type and rate on the{' '}
                            <Anchor component={Link} to={`/jobs/${pendingRow.jobId}`}>
                                job requirement
                            </Anchor>{' '}
                            first.
                        </Alert>
                    ) : null}

                    <Select
                        label="Job"
                        data={jobs.map((job) => ({
                            value: job.id,
                            label: `${job.name} (${formatJobDate(job.startDate)})`,
                        }))}
                        value={jobId}
                        onChange={setJobId}
                        disabled={pendingRow != null}
                    />

                    <Text size="sm">
                        Equipment: <strong>{equipmentLabel}</strong>
                    </Text>

                    {pendingRow?.thirdPartyRentalCompanyName ? (
                        <Text size="sm">{pendingRow.thirdPartyRentalCompanyName}</Text>
                    ) : null}
                    {pendingRow?.rentalAssetId == null ? (
                        <NumberInput
                            label="Quantity"
                            value={quantity}
                            onChange={setQuantity}
                            min={1}
                            max={maxQuantity}
                            allowDecimal={false}
                        />
                    ) : null}

                    <Select
                        label="Location"
                        placeholder="Select deployment location"
                        data={[
                            ...locationOptions,
                            {value: ADD_NEW_LOCATION_VALUE, label: 'Add new location…'},
                        ]}
                        value={locationId}
                        onChange={(value) => {
                            if (value === ADD_NEW_LOCATION_VALUE) {
                                setLocationModalOpen(true)
                                return
                            }
                            setLocationId(value)
                        }}
                        searchable
                    />

                    <Group align="flex-end" gap="sm">
                        <DateInput
                            label="Deployed on"
                            value={deployedOn}
                            onChange={(value) => {
                                if (value) {
                                    setDeployedOn(toLocalDate(value))
                                }
                            }}
                            maxDate={businessCalendarDate()}
                            style={{flex: 1}}
                        />
                        <Button
                            variant="default"
                            onClick={() => setDeployedOn(businessCalendarDate())}
                        >
                            Today
                        </Button>
                    </Group>
                    <TimeInput
                        label="Deployment time (optional)"
                        value={deployedTime}
                        onChange={(event) => setDeployedTime(event.currentTarget.value)}
                    />

                    {startsAfterToday && pendingRow ? (
                        <Alert color="yellow" title="Before job start">
                            Job starts {format(pendingRow.jobScheduledDate, 'MMM d, yyyy')}. Rental
                            starts on the deploy date.
                        </Alert>
                    ) : null}

                    {isBackdated ? (
                        <Alert color="yellow" title="Backdated deployment">
                            Rental billing will start from {format(deployedOn, 'MMM d, yyyy')}.
                        </Alert>
                    ) : null}

                    <Paper withBorder p="sm" radius="md">
                        <Stack gap={4}>
                            <Text size="sm" fw={600}>
                                Terms locked at deployment
                            </Text>
                            <Text size="sm">{formatRentalTermsLabel(pendingRow ?? {})}</Text>
                            <Text size="xs" c="dimmed">
                                These terms are permanent once equipment is deployed.
                            </Text>
                        </Stack>
                    </Paper>

                    <Group justify="flex-end" gap="sm">
                        <Button variant="default" onClick={handleClose}>
                            Cancel
                        </Button>
                        <Button
                            onClick={() => void handleSubmit()}
                            loading={deployEquipment.isPending}
                            disabled={!canSubmit}
                        >
                            Deploy
                        </Button>
                    </Group>
                </Stack>
            </Modal>

            <LocationEditModal
                isOpen={locationModalOpen}
                onClose={() => setLocationModalOpen(false)}
                onSave={handleSaveNewLocation}
                mode="create-new"
                source="equipment_deployment_editor"
                customerId={customerId}
                showSaveCheckbox={false}
            />
        </>
    )
}
